import type { JSONSchema7 } from "json-schema";
import type { LucideIcon } from "lucide-react";
import type { ToolApproval } from "@/components/NewAndIncredibleChatWidget/approval/approvalView";
import { ObservableRegistry } from "./observable-registry";

export interface GenieToolDefinition {
  name: string;
  description: string;
  parameters: JSONSchema7;
  requires_approval?: boolean;
}

// How the tool's calls render in the chat activity feed. Frontend tools are
// defined here, so their display lives here too - the chat widget resolves it
// generically instead of hardcoding per tool (backend tools carry display in
// their provider metadata instead). Never serialized: the registry's snapshot
// projector strips everything beyond GenieToolDefinition.
export interface GenieToolDisplay {
  icon: LucideIcon;
  messageStart: (input: Record<string, unknown>) => string;
  messageEnd: (input: Record<string, unknown>) => string;
  messageError: string;
  // For a tool that requires approval: what it does ("Go to this page"), named
  // on its approval card, and what "Always allow" covers.
  action?: string;
  grant?: string;
  // What its approval card shows, declared as backend tools declare theirs.
  approval?: ToolApproval;
}

export interface GenieTool extends GenieToolDefinition {
  execute: (input: Record<string, unknown>) => Promise<unknown>;
  display?: GenieToolDisplay;
}

export type GenieToolRunResult =
  | { ok: true; output: unknown }
  | { ok: false; error: string };

class GenieToolRegistry extends ObservableRegistry<
  GenieTool,
  GenieToolDefinition
> {
  constructor() {
    super(({ name, description, parameters, requires_approval }) => ({
      name,
      description,
      parameters,
      ...(requires_approval ? { requires_approval } : {}),
    }));
  }

  register(tool: GenieTool): () => void {
    return this.upsert(tool.name, tool);
  }

  registerMany(tools: GenieTool[]): () => void {
    return this.upsertMany(tools.map((t) => [t.name, t]));
  }

  getDefinitions(): GenieToolDefinition[] {
    return this.getSnapshot();
  }

  async run(
    toolName: string,
    input: unknown
  ): Promise<GenieToolRunResult | null> {
    const tool = this.get(toolName);
    if (!tool) return null;
    try {
      const output = await tool.execute(
        (input as Record<string, unknown>) ?? {}
      );
      return { ok: true, output };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : "Tool execution failed",
      };
    }
  }
}

export const genieTools = new GenieToolRegistry();
